import React, { useState, useEffect, useLayoutEffect } from "react";
import "../../css/InputData.css";
import {
  formatField,
  reformatField,
  convertToNative,
  checkFormat,
} from "../../js/format";
import { inputOnEnter } from "../../js/utils";

const formatFixedDecimals = (numValue, decimales, type) => {
  const fixed = numValue.toFixed(decimales);
  let display = fixed.replace(".", ",");

  if (type === "type-positive-optofloat") {
    display = "+" + display.replace("-", "");
  } else if (type === "type-optofloat" && display[0] !== "-") {
    display = "+" + display;
  }

  return display;
};

export default function InputDataControl(props) {
  const [value, setValue] = useState(null);
  const [formattedValue, setFormattedValue] = useState(null);
  const [isTyping, setIsTyping] = useState(false);

  let inputRef = React.useRef();
  const cursorPosRef = React.useRef(null);

  const getInputSize = () => {
    const sizeClass = {
      0: "input-full",
      1: "input-small",
      2: "input-medium",
      3: "input-large",
      4: "input-super-small",
      5: "input-super-small-2",
      6: "input-full-flex",
      7: "input-small-3",
      8: "input-small-4",
    };
    return sizeClass[props.size] || "";
  };

  useLayoutEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        cursorPosRef.current,
        cursorPosRef.current,
      );
      cursorPosRef.current = null;
    }
  }, [formattedValue]);

  useEffect(() => {
    if (props.input != null && !isTyping) {
      const inputStr = props.input.toString();
      const numDecimales = props.numDecimales ?? "";
      const numValue = parseFloat(inputStr);

      setValue(inputStr);

      if (numDecimales !== "" && !isNaN(numValue)) {
        setFormattedValue(
          formatFixedDecimals(numValue, parseInt(numDecimales, 10), props.type),
        );
      } else {
        setFormattedValue(reformatField(inputStr, props.type));
      }
    }
  }, [props.input, isTyping, props.numDecimales, props.type]);

  return (
    <div
      className={`input-data ${getInputSize()} ${
        props.extra ? props.extra : ""
      } ${props.error ? "input-error" : ""} ${props.bkg ? "marked-bkg" : ""} ${
        props.inactive ? "inactive-bkg" : ""
      } ${props.forceCenter ? "content-center" : ""} ${props.customStyle || ""}`}
    >
      <input
        ref={inputRef}
        id={props.id}
        name={props.name}
        tabIndex={props.inactive ? -1 : props.tabIndex}
        type={props.type ? props.type : "text"}
        disabled={props.inactive}
        maxLength={props.maxLength}
        onChange={(e) => {
          setIsTyping(true);

          cursorPosRef.current = inputRef.current?.selectionStart ?? null;

          const fValue = formatField(
            inputRef.current,
            e.target.value,
            props.type,
            props.dontForceUppercase,
          );
          setFormattedValue(fValue);

          const nValue = convertToNative(
            reformatField(fValue, props.type),
            props.type,
          );
          setValue(nValue);

          props.onChange(nValue);
        }}
        value={formattedValue ?? ""}
        onBlur={(e) => {
          setIsTyping(false);

          const rValue = reformatField(e.target.value, props.type);
          setFormattedValue(rValue);

          const nValue = convertToNative(rValue, props.type);
          setValue(nValue);

          if (props.onMalformed) {
            if (!checkFormat(rValue, props.type)) {
              props.onMalformed();
            }
          }

          const maxValue = props.maxValue ?? "";
          const minValue = props.minValue ?? "";
          const numDecimales = props.numDecimales ?? "";
          const numValue = parseFloat(nValue);
          let valorValido = true;

          if (!isNaN(numValue) && maxValue !== "" && numValue > parseFloat(maxValue)) {
            valorValido = false;
            props.createPopupQuestion(
              `El valor máximo debe ser ${maxValue}.`,
              () => {
                props.removePopupQuestion();
                inputRef.current?.focus();
                inputRef.current?.select();
              },
              null,
              "Aceptar",
            );
          } else if (
            !isNaN(numValue) &&
            minValue !== "" &&
            numValue < parseFloat(minValue)
          ) {
            valorValido = false;
            props.createPopupQuestion(
              `El valor mínimo debe ser ${minValue}.`,
              () => {
                props.removePopupQuestion();
                inputRef.current?.focus();
                inputRef.current?.select();
              },
              null,
              "Aceptar",
            );
          } else if (!isNaN(numValue) && (props.step ?? "") !== "") {
            const stepValue = parseFloat(props.step);
            const ratio = numValue / stepValue;
            const isCompatible = Math.abs(ratio - Math.round(ratio)) < 1e-6;

            if (!isCompatible) {
              valorValido = false;
              props.createPopupQuestion(
                `El valor introducido debe tener redondeo a ${props.step}.`,
                () => {
                  props.removePopupQuestion();
                },
                () => {
                  props.removePopupQuestion();
                  inputRef.current?.focus();
                  inputRef.current?.select();
                },
                "Mantener",
                "Cambiar",
              );
            }
          }

          let finalValue = nValue;

          if (valorValido && !isNaN(numValue) && numDecimales !== "") {
            const decimales = parseInt(numDecimales, 10);
            finalValue = numValue.toFixed(decimales);
            setValue(finalValue);
            setFormattedValue(formatFixedDecimals(numValue, decimales, props.type));
            props.onChange(finalValue);
          }

          if (props.onBlur) {
            props.onBlur(finalValue);
          }
        }}
        onKeyUp={(e) => {
          if (e.key === "Enter") {
            if (props.onEnter) {
              props.onEnter();
            } else {
              inputOnEnter(e);
            }
          }
        }}
        onFocus={(e) => {
          if (props.onFocus) {
            props.onFocus();
          }
        }}
      ></input>
      {props.icon && (
        <button
          onClick={() => {
            props.onClick();
          }}
        >
          <img src={props.icon} loading="lazy" alt="Icon" />
        </button>
      )}
    </div>
  );
}
